import { useState, type FormEvent } from 'react'
import { FLAVOR } from '../flavor/flavor'
import { BrandMark } from '@foroba/ui'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Capacitor } from '@capacitor/core'
import { login, loginWithMfa, sendLoginEmailOtp, register, forgotPasswordRequest, forgotPasswordConfirm, setBiometricEnabled } from '../auth/api'
import { pickMfaMethods, type LoginMfaMethod } from '../auth/mfaMethods'
import { useTranslation } from 'react-i18next'
import {
  checkBiometricAvailability,
  authenticate,
  biometryLabel,
} from '../native/biometric'
import { PasswordField } from '../components/PasswordField'
import styles from './LoginPage.module.css'

type View = 'login' | 'mfa' | 'register' | 'forgot-email' | 'forgot-confirm'

export function LoginPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [view, setView] = useState<View>('login')

  // Login state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [biometricOffer, setBiometricOffer] = useState<{ label: string } | null>(null)

  // MFA state (M1)
  const [mfaMethods, setMfaMethods] = useState<LoginMfaMethod[]>(['totp'])
  const [mfaMethod, setMfaMethod] = useState<LoginMfaMethod>('totp')
  const [mfaCode, setMfaCode] = useState('')
  const [otpSent, setOtpSent] = useState(false)

  // Register state
  const [regEmail, setRegEmail] = useState('')
  const [regEmailConfirm, setRegEmailConfirm] = useState('')
  const [regName, setRegName] = useState('')
  const [regPassword, setRegPassword] = useState('')
  const [regPasswordConfirm, setRegPasswordConfirm] = useState('')
  const [regTos, setRegTos] = useState(false)
  const [regSuccess, setRegSuccess] = useState(false)

  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotCode, setForgotCode] = useState('')
  const [forgotNewPwd, setForgotNewPwd] = useState('')
  const [forgotNewPwdConfirm, setForgotNewPwdConfirm] = useState('')
  const [forgotSuccess, setForgotSuccess] = useState(false)

  function reset(v: View) {
    setError(null)
    setView(v)
  }

  // ── Login ──────────────────────────────────────────────────────────────────
  async function finishLogin() {
    await qc.invalidateQueries({ queryKey: ['session'] })
    if (Capacitor.isNativePlatform()) {
      const avail = await checkBiometricAvailability()
      if (avail.available) {
        setBiometricOffer({ label: biometryLabel(avail.type) })
        return
      }
    }
    await navigate('/')
  }

  async function handleLogin(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const result = await login(email, password)
      if (result.ok) {
        await finishLogin()
      } else if (result.mfa_required) {
        const methods = pickMfaMethods(result.methods)
        setMfaMethods(methods)
        setMfaMethod(methods[0])
        setMfaCode('')
        setOtpSent(false)
        setView('mfa')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  async function handleSendOtp() {
    setError(null)
    try {
      await sendLoginEmailOtp(email)
      setOtpSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send code')
    }
  }

  async function handleMfa(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await loginWithMfa(email, mfaCode.trim(), mfaMethod)
      await finishLogin()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code')
    } finally {
      setLoading(false)
    }
  }

  async function handleEnableBiometric() {
    if (!biometricOffer) return
    const ok = await authenticate(`Verify ${biometricOffer.label} to enable it for future logins`)
    if (ok) setBiometricEnabled(true)
    setBiometricOffer(null)
    await navigate('/')
  }

  // ── Register ───────────────────────────────────────────────────────────────
  async function handleRegister(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await register({
        email: regEmail,
        email_confirm: regEmailConfirm,
        name: regName,
        password: regPassword,
        password_confirm: regPasswordConfirm,
        tos_accepted: regTos,
      })
      setRegSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  // ── Forgot password ────────────────────────────────────────────────────────
  async function handleForgotRequest(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await forgotPasswordRequest(forgotEmail)
      setView('forgot-confirm')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed')
    } finally {
      setLoading(false)
    }
  }

  async function handleForgotConfirm(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await forgotPasswordConfirm({
        email: forgotEmail,
        code: forgotCode,
        new_password: forgotNewPwd,
        new_password_confirm: forgotNewPwdConfirm,
      })
      setForgotSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed')
    } finally {
      setLoading(false)
    }
  }

  // ── Biometric offer ────────────────────────────────────────────────────────
  if (biometricOffer) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>Enable {biometricOffer.label}?</h1>
          <p className={styles.subtitle}>
            Sign in faster next time using {biometricOffer.label} instead of your password.
          </p>
          <div className={styles.form}>
            <button className={styles.button} type="button" onClick={handleEnableBiometric}>
              Enable {biometricOffer.label}
            </button>
            <button
              className={styles.button}
              type="button"
              onClick={async () => { setBiometricOffer(null); await navigate('/') }}
              style={{ background: 'transparent', color: 'var(--color-text-secondary, #94a3b8)', marginTop: '8px' }}
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Register success ───────────────────────────────────────────────────────
  if (view === 'register' && regSuccess) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>Account created</h1>
          <p className={styles.subtitle}>Check your email for a verification link, then sign in.</p>
          <div className={styles.form}>
            <button className={styles.button} type="button" onClick={() => { setRegSuccess(false); reset('login') }}>
              Sign in
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Forgot password success ────────────────────────────────────────────────
  if (forgotSuccess) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>Password reset</h1>
          <p className={styles.subtitle}>Your password has been updated. You can now sign in.</p>
          <div className={styles.form}>
            <button className={styles.button} type="button" onClick={() => { setForgotSuccess(false); reset('login') }}>
              Sign in
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── MFA challenge (M1) ─────────────────────────────────────────────────────
  if (view === 'mfa') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>{t('mobile.mfa.title')}</h1>
          <p className={styles.subtitle}>
            {mfaMethod === 'totp' ? t('mobile.mfa.totpHint') : t('mobile.mfa.emailHint', { email })}
          </p>
          <form onSubmit={handleMfa} className={styles.form} noValidate>
            {mfaMethods.length > 1 && (
              <div role="radiogroup" aria-label={t('mobile.mfa.method')} style={{ display: 'flex', gap: 8 }}>
                {mfaMethods.map((m) => (
                  <button key={m} type="button" role="radio" aria-checked={mfaMethod === m}
                    className={styles.link}
                    style={{ fontWeight: mfaMethod === m ? 600 : 400, textDecoration: mfaMethod === m ? 'underline' : 'none' }}
                    onClick={() => { setMfaMethod(m); setMfaCode(''); setError(null) }}>
                    {t(`mobile.mfa.methods.${m}`)}
                  </button>
                ))}
              </div>
            )}
            {mfaMethod === 'email_otp' && (
              <button className={styles.link} type="button" onClick={handleSendOtp} disabled={loading}>
                {otpSent ? t('mobile.mfa.resend') : t('mobile.mfa.send')}
              </button>
            )}
            <label className={styles.label}>
              {t('mobile.mfa.code')}
              <input className={styles.input} type="text" inputMode="numeric" autoComplete="one-time-code"
                pattern="[0-9]*" maxLength={8} value={mfaCode} autoFocus
                onChange={(e) => setMfaCode(e.target.value.replace(/\s/g, ''))} required disabled={loading} />
            </label>
            {error && <p className={styles.error} role="alert">{error}</p>}
            <button className={styles.button} type="submit" disabled={loading || mfaCode.length < 6}>
              {loading ? t('mobile.mfa.verifying') : t('mobile.mfa.verify')}
            </button>
            <button className={styles.link} type="button" onClick={() => reset('login')}>
              {t('mobile.mfa.back')}
            </button>
          </form>
        </div>
      </div>
    )
  }

  // ── Register form ──────────────────────────────────────────────────────────
  if (view === 'register') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>Create account</h1>
          <p className={styles.subtitle}>Join {FLAVOR.appName}</p>
          <form onSubmit={handleRegister} className={styles.form} noValidate>
            <label className={styles.label}>
              Name
              <input className={styles.input} type="text" autoComplete="name" value={regName}
                onChange={(e) => setRegName(e.target.value)} required disabled={loading} />
            </label>
            <label className={styles.label}>
              Email
              <input className={styles.input} type="email" autoComplete="email" value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)} required disabled={loading} />
            </label>
            <label className={styles.label}>
              Confirm email
              <input className={styles.input} type="email" autoComplete="email" value={regEmailConfirm}
                onChange={(e) => setRegEmailConfirm(e.target.value)} required disabled={loading} />
            </label>
            <label className={styles.label}>
              Password
              <PasswordField inputClassName={styles.input} autoComplete="new-password" value={regPassword}
                onChange={(e) => setRegPassword(e.target.value)} required disabled={loading} />
            </label>
            <label className={styles.label}>
              Confirm password
              <PasswordField inputClassName={styles.input} autoComplete="new-password" value={regPasswordConfirm}
                onChange={(e) => setRegPasswordConfirm(e.target.value)} required disabled={loading} />
            </label>
            <label className={styles.checkboxLabel}>
              <input type="checkbox" checked={regTos} onChange={(e) => setRegTos(e.target.checked)} />
              I accept the Terms of Service and Privacy Policy
            </label>
            {error && <p className={styles.error} role="alert">{error}</p>}
            <button className={styles.button} type="submit" disabled={loading || !regTos}>
              {loading ? 'Creating account…' : 'Create account'}
            </button>
            <button className={styles.link} type="button" onClick={() => reset('login')}>
              Already have an account? Sign in
            </button>
          </form>
        </div>
      </div>
    )
  }

  // ── Forgot password — enter email ──────────────────────────────────────────
  if (view === 'forgot-email') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>Reset password</h1>
          <p className={styles.subtitle}>Enter your email and we'll send you a reset code.</p>
          <form onSubmit={handleForgotRequest} className={styles.form} noValidate>
            <label className={styles.label}>
              Email
              <input className={styles.input} type="email" autoComplete="email" value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)} required disabled={loading} />
            </label>
            {error && <p className={styles.error} role="alert">{error}</p>}
            <button className={styles.button} type="submit" disabled={loading}>
              {loading ? 'Sending…' : 'Send reset code'}
            </button>
            <button className={styles.link} type="button" onClick={() => reset('login')}>
              Back to sign in
            </button>
          </form>
        </div>
      </div>
    )
  }

  // ── Forgot password — enter code + new password ────────────────────────────
  if (view === 'forgot-confirm') {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>New password</h1>
          <p className={styles.subtitle}>Enter the code sent to {forgotEmail} and choose a new password.</p>
          <form onSubmit={handleForgotConfirm} className={styles.form} noValidate>
            <label className={styles.label}>
              Reset code
              <input className={styles.input} type="text" inputMode="numeric" autoComplete="one-time-code"
                value={forgotCode} onChange={(e) => setForgotCode(e.target.value)} required disabled={loading} />
            </label>
            <label className={styles.label}>
              New password
              <PasswordField inputClassName={styles.input} autoComplete="new-password" value={forgotNewPwd}
                onChange={(e) => setForgotNewPwd(e.target.value)} required disabled={loading} />
            </label>
            <label className={styles.label}>
              Confirm new password
              <PasswordField inputClassName={styles.input} autoComplete="new-password" value={forgotNewPwdConfirm}
                onChange={(e) => setForgotNewPwdConfirm(e.target.value)} required disabled={loading} />
            </label>
            {error && <p className={styles.error} role="alert">{error}</p>}
            <button className={styles.button} type="submit" disabled={loading}>
              {loading ? 'Resetting…' : 'Reset password'}
            </button>
            <button className={styles.link} type="button" onClick={() => reset('forgot-email')}>
              Resend code
            </button>
          </form>
        </div>
      </div>
    )
  }

  // ── Login form (default) ───────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <BrandMark brand={FLAVOR.dataApp} size={44} className={styles.brandMark} />
        <h1 className={styles.title}>{FLAVOR.appName}</h1>
        <p className={styles.subtitle}>Sign in to continue</p>

        <form onSubmit={handleLogin} className={styles.form} noValidate>
          <label className={styles.label}>
            Email
            <input
              className={styles.input}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          </label>

          <label className={styles.label}>
            Password
            <PasswordField
              inputClassName={styles.input}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
          </label>

          {error && <p className={styles.error} role="alert">{error}</p>}

          <button className={styles.button} type="submit" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>

          <button className={styles.link} type="button" onClick={() => reset('forgot-email')}>
            Forgot password?
          </button>

          <button className={styles.link} type="button" onClick={() => reset('register')}>
            Create account
          </button>
        </form>
      </div>
    </div>
  )
}
